import { apiClient } from '@/api/api-client'

export function fetchUserInfo({ axiosConfig = {} } = {}) {
  return apiClient.get('/account/user/info', {
    ...axiosConfig,
  })
}

/**
 * Get account payment info
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<object>} - Account Payment info
 * @throws {Error} ApiError
 */
export async function fetchPaymentInfo({ axiosConfig = {} } = {}) {
  return await apiClient.get('/account/payment/info', {
    ...axiosConfig,
  })
}

/**
 * Fetch account Stripe invoices
 * @param {object} params - Request parameters
 * @param {string} params.invoiceId - Invoice ID
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<Array>} - Account Stripe invoices
 * @throws {Error} ApiError
 */
export async function fetchInvoiceDetails({ params, axiosConfig = {} }) {
  return apiClient.get(`/account/payment/invoice-detail-v2/${params.invoiceId}`, {
    ...axiosConfig,
  })
}

/**
 * Create a setup intent
 * @param {object} params - Request parameters
 * @param {string} params.stripeProductId - Payment method ID
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<object>} - Setup intent
 * @throws {Error} ApiError
 */
export async function createSetupIntent({ params, axiosConfig = {} }) {
  return apiClient.post('/account/payment/stripe/setup-intent', null, {
    params: {
      paymentMethodId: params.paymentMethodId,
    },
    ...axiosConfig,
  })
}

/**
 * Update default payment method
 * @param {object} params - Request parameters
 * @param {string} params.paymentMethodId - Payment method ID
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<object>} - Updated payment method
 * @throws {Error} ApiError
 */
export async function updateDefaultPaymentMethod({ params, axiosConfig = {} }) {
  return apiClient.put(`/account/payment/payment-method/${params.paymentMethodId}/default`, null, {
    ...axiosConfig,
  })
}

/**
 * Delete payment method
 * @param {object} params - Request parameters
 * @param {string} params.paymentMethodId - Payment method ID
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<void>}
 * @throws {Error} ApiError
 */
export async function deletePaymentMethod({ params, axiosConfig = {} }) {
  return apiClient.delete(`/account/payment/payment-method/${params.paymentMethodId}`, {
    ...axiosConfig,
  })
}

/**
 * Retrive coupon details by coupon ID
 * @param {object} params - Request parameters
 * @param {string} params.couponCode - Coupon ID
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<object>} - Coupon details
 * @throws {Error} ApiError
 */
export async function fetchCouponDetails({ params, axiosConfig = {} }) {
  return apiClient.get(`/account/payment/coupon/${params.couponId}`, {
    ...axiosConfig,
  })
}

/**
 * Subscribe to a product
 * @param {object} params - Request parameters
 * @param {object} params.body - Request body
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<object>} - Subscription details
 * @throws {Error} ApiError
 */
export async function subscribe({ params, axiosConfig = {} }) {
  return apiClient.post('/account/payment/subscribe', params.body, {
    ...axiosConfig,
  })
}

/**
 * Fetch product details by product ID
 * @param {object} params - Request parameters
 * @param {string} params.productId - Product ID
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<object>} - Product details
 * @throws {Error} ApiError
 */
export async function fetchProductDetails({ params, axiosConfig = {} }) {
  return apiClient.get(`/account/payment/product/info?product=${params.productId}`, {
    ...axiosConfig,
  })
}

/**
 * Fetch user products
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<Array>} - User products
 * @throws {Error} ApiError
 */
export async function fetchUserProducts({ axiosConfig = {} } = {}) {
  return apiClient.get('/account/products', {
    ...axiosConfig,
  })
}

/**
 * Delete Account
 * @param {number} params.accountId - ID of the account to delete
 * @param {object} axiosConfig - (optional) Axios config
 */
export async function deleteAccount({ params, axiosConfig = {} }) {
  return apiClient.delete(`/account/${params.accountId}`, {
    ...axiosConfig,
  })
}

/**
 * Delete User Account
 * @param {object} axiosConfig - (optional) Axios config
 */
export async function deleteUserAccount({ axiosConfig = {} } = {}) {
  return apiClient.delete(`/account/delete/user`, {
    ...axiosConfig,
  })
}

/**
 * Reactivate account
 * @param {number} params.accountId - ID of the account to delete
 * @param {object} axiosConfig - (optional) Axios config
 */
export async function reactivateAccount({ axiosConfig = {} } = {}) {
  return apiClient.post(`/account/cancel-account-deletion`, {
    ...axiosConfig,
  })
}

/**
 * Delete subscription
 * Endpoint: DELETE /account/payment/subscription/{subscriptionId}
 *
 * @param {number} params.subscriptionId - ID of the new subscription
 * @param {object} axiosConfig - (optional) Axios config
 */
export async function deleteSubscription({ params, axiosConfig = {} }) {
  return apiClient.patch(`/account/payment/subscription/${params.subscriptionId}`, params.body, {
    ...axiosConfig,
  })
}

/**
 * proration preview
 * @param {object} params - Request parameters
 * @param {object} params.body - Request body
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<object>} - Subscription details
 * @throws {Error} ApiError
 */
export async function proration({ params, axiosConfig = {} }) {
  return apiClient.get(`/account/payment/invoice/product/${params.productId}/period/${params.paymentPeriod}/preview`, {
    params: {
      couponCode: params?.couponCode,
    },
    ...axiosConfig,
  })
}

/**
 * This endpoint is designed to be called before the payment so that the user does not get stuck on the Stripe side.
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<null>} - empty response
 * @throws {Error} ApiError
 */
export async function paymentAttempt({ axiosConfig = {} } = {}) {
  return apiClient.get(`/account/payment/payment-attempt`, {
    ...axiosConfig,
  })
}

/**
 * Fetch team members
 * @param {object} axiosConfig - Axios config
 * @returns {Promise<Array>} - Team members
 * @throws {Error} ApiError
 */
export async function fetchTeamMembers({ axiosConfig = {} } = {}) {
  return apiClient.get(`/account/team-member`, {
    ...axiosConfig,
  })
}
